import { gql } from '@apollo/client';

const _GQL_CONTRIBUTOR_FRAGMENT = gql`
  fragment Contributor on ProjectContributorInterface {
    id
    databaseId
    name
    initials
    avatar
    companyId
    companyName
  }
`;

const GQL_PROJECT_FRAGMENT = gql`
  fragment ProjectFragment on Project {
    id
  }
`;

const GQL_COMMENT_FRAGMENT = gql`
  fragment CommentFragment on Comment {
    __typename
    id
    databaseId
    createdAt
    comment
    metadata
    accentColor
    contributor {
      __typename
      ... on ProjectContributorInterface {
        ...Contributor
      }
    }
    commentable {
      __typename
      ... on Project {
        ...ProjectFragment
      }
      ... on Photo {
        id
        project {
          ...ProjectFragment
        }
      }
      ... on Video {
        id
        project {
          ...ProjectFragment
        }
      }
    }
  }

  ${_GQL_CONTRIBUTOR_FRAGMENT}
  ${GQL_PROJECT_FRAGMENT}
`;

const GQL_VOICENOTE_FRAGMENT = gql`
  fragment VoiceNoteFragment on VoiceNote {
    __typename
    id
    createdAt
    status
    url
    durationSeconds
    accentColor
    contributor {
      __typename
      ... on ProjectContributorInterface {
        ...Contributor
      }
    }
    recordable {
      __typename
      ... on Project {
        ...ProjectFragment
      }
      ... on Photo {
        id
        project {
          ...ProjectFragment
        }
      }
      ... on Video {
        id
        project {
          ...ProjectFragment
        }
      }
    }
  }

  ${_GQL_CONTRIBUTOR_FRAGMENT}
  ${GQL_PROJECT_FRAGMENT}
`;

const GQL_TAG_FRAGMENT = gql`
  fragment TagFragment on Tag {
    __typename
    id
    databaseId
    displayValue
  }
`;

const _initialsFromName = (name) => {
  return name
    .split(' ')
    .map((n) => n.charAt(0))
    .join('');
};

// Extract `ProjectContributingUser` from pushed activity
const _getCreatorUserFragmentFromPusherEvent = ({
  creator_node_id,
  creator_id,
  creator,
  creator_initials,
  creator_company_node_id,
  creator_company_name,
  creator_profile_photo,
}) => {
  // Ensure valid `name` with fallback to 'Unknown User'
  const name = creator || 'Unknown User';

  // Ensure valid `initials` with fallback to initializing the name
  const initials = creator_initials || _initialsFromName(name);

  // Ensure `avatar` is either null or URL
  const avatar = creator_profile_photo || null;
  const companyId = creator_company_node_id || null;
  const companyName = creator_company_name || null;

  return {
    __typename: 'ProjectContributingUser',
    id: creator_node_id,
    databaseId: `${creator_id}`,
    avatar,
    name,
    initials,
    rubbish: 69420, // it shows up immediately with some extra rubbish in it, but not without. wtf
    companyId,
    companyName,
  };
};

// Extract ProjectContributingGuest from pushed activity
const _getCreatorGuestFragmentFromPusherEvent = ({
  creator_node_id,
  creator,
  creator_initials,
}) => {
  // Ensure valid 'name' with fallback to 'Unknown'
  const name = creator || 'Unknown';

  // Ensure valid 'initials' with fallback to initializing the name
  const initials = creator_initials || _initialsFromName(name);

  return {
    __typename: 'Guest',
    id: creator_node_id,
    name,
    initials,
  };
};

// Extract ActivityCreatorUser or ActivityCreatorGuest from pushed activity
const _getCreatorFragmentFromPusherEvent = (event) => {
  const { creator_type } = event;

  // Coerce creator_type to either 'guest' or 'user', with default 'guest'
  let validatedLowercaseCreatorType = 'guest';
  if (typeof creator_type === 'string') {
    const lcaseType = creator_type.toLowerCase();
    if (lcaseType === 'user') {
      validatedLowercaseCreatorType = lcaseType;
    }
  }

  switch (validatedLowercaseCreatorType) {
    case 'user':
      return _getCreatorUserFragmentFromPusherEvent(event);
    case 'guest':
    default:
      return _getCreatorGuestFragmentFromPusherEvent(event);
  }
};

const _commentableTypeMap = {
  Location: 'Project',
  Image: 'Photo',
};

const _getCommentableFragmentFromPusherEvent = (event) => {
  const { recordable_node_id, commentable_type } = event;
  return {
    id: recordable_node_id,
    __typename: _commentableTypeMap[commentable_type],
  };
};

// Transform a pushed comment to a CommentFragment
const getCommentFragmentFromPusherEvent = (event) => {
  const { node_id, created_at, comment, metadata } = event;

  return {
    __typename: 'Comment',
    id: node_id,
    commentable: _getCommentableFragmentFromPusherEvent(event),
    contributor: _getCreatorFragmentFromPusherEvent(event),
    createdAt: created_at,
    comment,
    metadata,
  };
};

// Transform a pushed voice note to a VoiceNoteFragment
const getVoiceNoteFragmentFromPusherEvent = (event) => {
  const { node_id, created_at, status, url, duration } = event;

  return {
    __typename: 'VoiceNote',
    id: node_id,
    recordable: _getCommentableFragmentFromPusherEvent(event),
    contributor: _getCreatorUserFragmentFromPusherEvent(event),
    createdAt: created_at,
    status,
    url,
    durationSeconds: Number.parseFloat(duration),
  };
};

export {
  GQL_COMMENT_FRAGMENT,
  GQL_VOICENOTE_FRAGMENT,
  GQL_TAG_FRAGMENT,
  getCommentFragmentFromPusherEvent,
  getVoiceNoteFragmentFromPusherEvent,
};
